import { Qualification } from "@/data/models/Qualification";
import { PlanningParameters } from "@/data/models/PlanningParameters";
import ProjectOverview from "./ProjectOverview";
import { getProjectStepContainer, getStepCreationDate } from "./ProjectSteps";
import { isFlexProject } from "./ProjectDetails";
import { SchedulingScore } from "@/utils/scheduling/SchedulingObjectiveFunction";
import { generateRandomUuid } from "@/utils/Utils";
import { DateTime } from "luxon";

export enum TaskType {
  LEAK_DETECTION,
  DAMAGE_ASSESSMENT,
  DRYING_SETUP,
  INTERMEDIATE_MEASUREMENT,
  DRYING_CONCLUSION,
  REPAIR,
}

export function getMinStartDateTimeForTaskType(
  targetDateTime: DateTime,
  taskType: TaskType,
  includeWeekends: boolean
) {
  const now = DateTime.fromJSDate(new Date());

  const canTaskStartToday =
    taskType == TaskType.DAMAGE_ASSESSMENT ||
    taskType == TaskType.LEAK_DETECTION;

  const minStartDateTime = canTaskStartToday
    ? now.plus({ hour: 1 })
    : now.plus({ day: 1 }).startOf("day");

  const dateTime =
    minStartDateTime.valueOf() > targetDateTime.valueOf()
      ? minStartDateTime
      : targetDateTime;

  if (includeWeekends || dateTime.weekday < 6) {
    return dateTime;
  }

  //Go to start of next week
  return dateTime.plus({ week: 1 }).startOf("week");
}

export interface Task {
  projectId: string;
  projectName: string;
  projectNumber: string;

  taskType: TaskType;
  projectOwnerId: string | null;
  projectSupervisorId: string | null;
  projectOwnerStaffNumber?: string | null;
  projectSupervisorStaffNumber?: string | null;

  earliestStartDate: Date;
  id: string;
  urgent: boolean;
  durationInMin: number;
  requiredQualification: Qualification[];
  planningParameters: PlanningParameters;
  note?: string;
  editEnabled?: boolean;
}

const temporaryTaskPrefix = "temporary_";

export function generateTemporaryTaskId() {
  return `${temporaryTaskPrefix}_${generateRandomUuid()}`;
}

export function isTemporaryTaskId(taskId: string) {
  return taskId.startsWith(temporaryTaskPrefix);
}

export interface ScheduledTask extends Task {
  startDate: Date;
  endDate: Date;
  userId: string;
  wasConfirmed: boolean;
  schedulingScore?: SchedulingScore;
}

export interface TaskSchedule {
  startDate?: Date;
  endDate?: Date;
  wasConfirmed?: boolean;
}

export interface FocusedTask extends Task, TaskSchedule {
  note?: string;
  userId?: string;
  showNote?: boolean;
  isMoving?: boolean;
  schedulingScore?: SchedulingScore;
}

export const TaskTypeAttributeName = [
  "leakDetection",
  "damageAssessment",
  "dryingSetup",
  "intermediateMeasurement",
  "dryingConclusion",
  "repair",
] as const;

export function getTaskTypeFieldName(taskType: TaskType) {
  switch (taskType) {
    case TaskType.LEAK_DETECTION:
      return "leakDetection";
    case TaskType.DAMAGE_ASSESSMENT:
      return "damageAssessment";
    case TaskType.DRYING_SETUP:
      return "dryingSetup";
    case TaskType.INTERMEDIATE_MEASUREMENT:
      return "intermediateMeasurement";
    case TaskType.DRYING_CONCLUSION:
      return "dryingConclusion";
    case TaskType.REPAIR:
      return "repair";
  }
}

export function localiseTaskType(vue: Vue, taskType: TaskType) {
  const taskTypeFieldName = getTaskTypeFieldName(taskType);
  return vue.$t(`scheduling.taskType.${taskTypeFieldName}`);
}

export function copyTask(
  source: Task,
  copyAllAttributesOfSource?: boolean
): Task {
  if (copyAllAttributesOfSource) {
    return {
      ...source,
    };
  }

  return {
    projectId: source.projectId,
    projectName: source.projectName,
    projectNumber: source.projectNumber,

    urgent: source.urgent,
    taskType: source.taskType,
    projectOwnerId: source.projectOwnerId,
    projectSupervisorId: source.projectSupervisorId,
    earliestStartDate: source.earliestStartDate,
    id: source.id,
    durationInMin: source.durationInMin,
    requiredQualification: source.requiredQualification,
    planningParameters: source.planningParameters,
  };
}

export function isScheduledTask(
  task: Task | ScheduledTask
): task is ScheduledTask {
  return (task as ScheduledTask)?.startDate !== undefined;
}

export function getEarliestTaskStartDate(
  taskType: TaskType,
  projectOverview: ProjectOverview
) {
  if (!isFlexProject(projectOverview)) {
    return new Date();
  }

  const projectStepContainer = getProjectStepContainer(projectOverview);

  if (!projectStepContainer) {
    return new Date();
  }

  switch (taskType) {
    case TaskType.LEAK_DETECTION:
      return (
        getStepCreationDate(projectStepContainer.firstContact) ??
        getStepCreationDate(projectStepContainer.projectApproved)
      );
    case TaskType.DAMAGE_ASSESSMENT:
      return (
        getStepCreationDate(projectStepContainer.leakDetectionResult) ??
        getStepCreationDate(projectStepContainer.firstContact) ??
        getStepCreationDate(projectStepContainer.projectApproved)
      );
    case TaskType.DRYING_SETUP:
      return (
        getStepCreationDate(projectStepContainer.offerResult) ??
        getStepCreationDate(projectStepContainer.damageAssessmentResult) ??
        getStepCreationDate(projectStepContainer.projectApproved)
      );
    case TaskType.INTERMEDIATE_MEASUREMENT:
      return (
        getStepCreationDate(
          projectStepContainer.intermediateMeasurementCompletions
        ) ??
        getStepCreationDate(projectStepContainer.dryingSetupCompletions) ??
        getStepCreationDate(projectStepContainer.offerResult) ??
        getStepCreationDate(projectStepContainer.damageAssessmentResult)
      );
    case TaskType.DRYING_CONCLUSION:
      return (
        getStepCreationDate(
          projectStepContainer.intermediateMeasurementCompletions
        ) ??
        getStepCreationDate(projectStepContainer.dryingSetupCompletions) ??
        getStepCreationDate(projectStepContainer.offerResult) ??
        getStepCreationDate(projectStepContainer.damageAssessmentResult)
      );
    case TaskType.REPAIR:
      return (
        getStepCreationDate(projectStepContainer.dryingConclusionResults) ??
        getStepCreationDate(
          projectStepContainer.intermediateMeasurementCompletions
        ) ??
        getStepCreationDate(projectStepContainer.dryingSetupCompletions)
      );
  }
}
